export interface MapeamentoColuna {
  origem: string;
  destino: string;
}

export interface BatchProgress {
  ok: number;
  err: number;
  total: number;
  done: boolean;
}

export const COLUNAS_REGISTRO: string[] = [
  'SEQUENCIA', 'DATADOC', 'DATAREC', 'VLRTOT', 'VLRLIQ', 'VLRTAXA',
  'VLRTAXAANT', 'VLRPREV', 'TIPO', 'NSU', 'TID', 'NUAUT', 'PEDIDO',
  'NOTA', 'ESTABELECIMENTO', 'NUCARTAO', 'MODALIDADE', 'BANDEIRA',
  'QTDPARCELA', 'PARCELA', 'STATUS', 'NUNOTA', 'NUFIN', 'NUFINNOVO',
  'LIBERADO', 'NSII', 'ID',
];

/**
 * Colunas visiveis do grid de registros (tela principal) com seus rotulos
 * PADRAO. A config pode sobrescrever cada rotulo via MKTPCONFCONCILIADORA.LABELSGRADE
 * (JSON { chave: rotulo }). Chave nao sobrescrita -> usa o padrao daqui.
 */
export interface GridCol {
  key: string;
  label: string;
}

export const GRID_COLS: GridCol[] = [
  { key: 'SEQUENCIA', label: 'Seq' },
  { key: 'DATADOC', label: 'Data Doc' },
  { key: 'VLRTOT', label: 'Vlr Total' },
  { key: 'VLRLIQ', label: 'Vlr Líquido' },
  { key: 'VLRPREV', label: 'Provisão' },
  { key: 'TIPO', label: 'Tipo' },
  { key: 'NSU', label: 'NSU' },
  { key: 'PEDIDO', label: 'Pedido' },
  { key: 'NOTA', label: 'Nota' },
  { key: 'NUNOTA', label: 'NUNOTA' },
  { key: 'STATUS', label: 'Status' },
  { key: 'LIBERADO', label: 'Liberado' },
];

export interface ConciliadoraConfig {
  NUCONF: number;
  CODECOMPANY: number;
  CODECONTRACT: number | null;
  CODEMKTP: number | null;
  DESCRICAO: string;
  VALIDA: string;
  ATIVO: string;
  SQLBUSCA: string | null;
  MAPEAMENTO: string | null;
  /** JSON { chaveColuna: rotuloCustom } — rotulos do grid por config. Ver GRID_COLS. */
  LABELSGRADE: string | null;
  /** JSON array de chaves de coluna VISIVEIS no grid, na ordem (item 2, grid
   *  dinamico). null/vazio => usa o default (GRID_COLS). Colunas fora de GRID_COLS
   *  (ex.: coluna nova da tabela) entram como colunas "extra" genericas. */
  COLUNASGRADE: string | null;
  IDCONFLOCALIZANOTA: number | null;
  CREATEDBY: string | null;
  CREATEDON: string | null;
  MODIFIEDBY: string | null;
  MODIFIEDON: string | null;
}

export interface TipoDpara {
  campo: string;
  valor: string;
}

export interface ConciliadoraTipoConfig {
  IDTIPO: number;
  NUCONF: number;
  CODECOMPANY: number;
  CODECONTRACT: number | null;
  CODEMKTP: number | null;
  TIPOS: string;
  DESCRICAO: string | null;
  IDCONF: number | null;
  PARAMETROS: string | null;
  /** Ordem de execucao no Processar (menor primeiro; a baixa deve ter o maior). */
  ORDEM: number | null;
  CREATEDON: string | null;
  MODIFIEDON: string | null;
}

export interface ConciliadoraCabecalho {
  CODIGO: number;
  NUCONF: number;
  /** Nro. Conciliacao (texto, opcional). Usado como @NRO_CONCILIACAO no SQLBUSCA. */
  NROCONCILIACAO: string | null;
  CODECOMPANY: number;
  CODECONTRACT: number | null;
  CODEMKTP: number | null;
  DTCRIACAO: string | null;
  DTBAIXA: string | null;
  AGENDARPROCESSAMENTO: string;
  STATUS: string;
  OBSERVACAO: string | null;
  CREATEDBY: string | null;
  MODIFIEDBY: string | null;
  MODIFIEDON: string | null;
}

export interface ConciliadoraRegistro {
  IDREGISTRO: number;
  CODIGO: number;
  CODECOMPANY: number;
  CODECONTRACT: number | null;
  CODEMKTP: number | null;
  SEQUENCIA: number | null;
  DATADOC: string | null;
  DATAREC: string | null;
  VLRTOT: number | null;
  VLRLIQ: number | null;
  VLRTAXA: number | null;
  VLRTAXAANT: number | null;
  VLRPREV: number | null;
  TIPO: string | null;
  NSU: string | null;
  TID: string | null;
  NUAUT: string | null;
  PEDIDO: string | null;
  NOTA: string | null;
  ESTABELECIMENTO: string | null;
  NUCARTAO: string | null;
  MODALIDADE: string | null;
  BANDEIRA: string | null;
  QTDPARCELA: number | null;
  PARCELA: number | null;
  STATUS: string | null;
  NUNOTA: string | null;
  NUFIN: string | null;
  NUFINNOVO: string | null;
  LIBERADO: string | null;
  NSII: string | null;
  /** Identificador de origem para dedup (NOT EXISTS na query de config). */
  ID: string | null;
  /** Status canonico gravado pelo fluxo (C/P/D/V/E/X). */
  STATUSCON?: string | null;
  /** Status EFETIVO derivado (STATUSCON preferido, LIBERADO fallback) — vem do getRegistros. */
  STATUSEFETIVO?: string | null;
  CREATEDON: string | null;
  MODIFIEDON: string | null;
}

export interface DashboardStats {
  total: number;
  liberados: number;
  pendentes: number;
  divergentes: number;
  erros: number;
  naoLocalizados: number;
  provisoes: number;
}

/** Metadados dos status canonicos (C/P/D/V/E/X): rotulo + cor + icone. Fonte
 * unica para grid, filtros, cards e resumo. */
export const STATUSCON_META: Record<string, { label: string; color: string; tag: string }> = {
  C: { label: 'Conciliado',     color: '#2FD37A', tag: 'green'  },
  P: { label: 'Pendente',       color: '#8c8c8c', tag: 'default' },
  D: { label: 'Divergente',     color: '#FF9A4D', tag: 'orange' },
  V: { label: 'Provisão',       color: '#4EA8FF', tag: 'blue'   },
  E: { label: 'Erro',           color: '#FF5C6C', tag: 'red'    },
  X: { label: 'Nota não encontrada', color: '#B79CFF', tag: 'purple' },
};

/** Ordem canonica dos status para dropdowns/filtros. */
export const STATUSCON_ORDER: string[] = ['C', 'P', 'D', 'V', 'E', 'X'];

/** Linha do resumo por TIPO de uma conciliacao (aba/janela de resumo).
 * Orientado a DINHEIRO (visao financeira): alem das contagens, traz o valor
 * liquido por status (pendente/liberado/divergente) para leitura de R$ e risco. */
export interface ConciliadoraResumoTipo {
  TIPO: string;
  total: number;
  pendentes: number;
  /** "liberados" = Conciliado (C). Nome mantido por compatibilidade da UI. */
  liberados: number;
  divergentes: number;
  erros: number;
  naoLocalizados: number;
  semNota: number;
  vlrLiq: number;
  vlrPrev: number;
  /** Valor liquido por status (R$). */
  vlrPendente: number;
  vlrLiberado: number;
  vlrDivergente: number;
  vlrErro: number;
  vlrNaoLocalizado: number;
}

export interface CompanyOption {
  codecompany: number;
  codecontract: number | null;
  codemktp?: number | null;
  companyname?: string;
  name?: string;
}

export interface MktpCfgOption {
  IDCONF: number;
  NAME: string;
  /** Valor cru retornado pelo backend ('S' | 'N' | null). Manter para auditoria. */
  NEWMODE?: string | null;
  /** True quando NEWMODE === 'S' (motor novo, requer endpoint /FlowServices/runOnline). */
  newMode?: boolean;
}

export type ProcessingAction = 'buscar' | 'calcular' | 'processar' | 'localizar' | null;

/** Contexto da chamada de IDCONF NEWMODE - injetado em __contextoAcao. */
export type FlowContextoAcao = 'PROCESSAR' | 'LOCALIZAR_NOTA' | 'REPROCESSAR';

/** Opcoes para executarConfiguracao/executarConfiguracaoLote/executarFlowOnline. */
export interface ExecutarOpts {
  /** Se true, roteia para /FlowServices/runOnline (motor novo). */
  newMode?: boolean;
  /** Acao logica enviada como __contextoAcao. */
  contextoAcao: FlowContextoAcao;
  /** PARAMETROS (DPARA) do tipo, ja parseado para object. */
  metadados?: Record<string, any> | null;
  /** Numero da configuracao (NUCONF) - rastreio. */
  nuconf?: number | null;
  /** Codigo do cabecalho da conciliacao (CODIGO) - rastreio. */
  codigoCab?: number | null;
  /** Empresa-alvo (default = empresa selecionada). */
  codecompany?: number | null;
  /** Marketplace (raro - default null). */
  codemktp?: number | null;
  /** Sequence opcional (default 0). */
  sequence?: number | null;
  /**
   * Quando true, propaga `__logEnabled: true` no `context` do payload para
   * que o backend grave o `flowExecutionList` em MKTPLGI.DATALOG (depende do
   * patch documentado em backend-java/FlowServices_log_patch.md). Permite ao
   * desenvolvedor consultar a execucao pela tela do Fluxo (Webhook Creator).
   * Default: true.
   */
  logEnabled?: boolean;
}

export const LIBERADO_STATES = {
  LIBERADO: 'Liberado',
  DIVERGENTE: 'Valor Divergente',
  PENDENTE: 'Pendente',
  ERRO_PREFIX: 'Erro:',
} as const;

// =====================================================================
// Modelos de monitoramento de fluxo NEWMODE assincrono (Fase B)
// =====================================================================

export type FlowJobStatusType =
  | 'PENDING'
  | 'RUNNING'
  | 'COMPLETED'
  | 'ERROR'
  | 'STOPPED'
  | 'NOT_FOUND';

/** Resposta de POST /FlowServices/runOnlineAsync. */
export interface FlowJobStart {
  ok: boolean;
  idjob?: string;
  threadName?: string;
  idconf?: number;
  rowsCount?: number;
  status?: FlowJobStatusType;
  message?: string;
}

/** Resposta de POST /FlowServices/jobStatus. */
export interface FlowJobStatus {
  idjob: string;
  threadName?: string;
  idconf?: number;
  rowsCount?: number;
  status: FlowJobStatusType;
  error: boolean;
  errorMessage: string | null;
  startedAt: number;
  finishedAt: number;
  elapsedMs: number;
  /** flowExecutionList JSON quando status=COMPLETED. */
  result: any | null;
  message?: string;
}

/** Step extraido do flowExecutionList (formato emitido pelo FlowHelper.toJson). */
export interface FlowExecutionStep {
  key: string;
  name?: string;
  type?: string;
  dhStart?: string;
  dhEnd?: string;
  durationMs?: number;
  parentExecutionId?: string;
  executionPath?: string;
  input?: any;
  output?: any;
  error?: boolean;
  errorMessage?: string;
}

/** Estado interno mantido pelo FlowMonitorService. */
export interface FlowMonitorState {
  idjob: string | null;
  threadName: string | null;
  idconf: number | null;
  status: FlowJobStatusType | 'IDLE' | 'PARTIAL';
  error: boolean;
  errorMessage: string | null;
  steps: FlowExecutionStep[];
  rawResult: any | null;
  startedAt: number | null;
  finishedAt: number | null;
  elapsedMs: number;
  rowsCount: number;
  title: string;
  contextoAcao: FlowContextoAcao | null;
  /** True enquanto a chamada inicial /runOnlineAsync ainda nao retornou. */
  starting: boolean;

  // -------------------- Extensoes para FlowExecutorService (Fase 1) --------------------
  /** UUID local do job no FlowExecutorService. Permite multi-job no monitor. */
  jobId?: string | null;
  /** Texto curto exibido na lista (ex: "Tipo SALE - 47 reg."). */
  label?: string | null;
  /** Contadores parciais reportados pelo backend (para Legacy/NEWMODE_SYNC). */
  rowsOk?: number;
  rowsErr?: number;
  /** Estrategia escolhida pelo executor para este job. */
  strategy?:
    | 'NEWMODE_ASYNC'
    | 'NEWMODE_SYNC'
    | 'LEGACY_ASYNC'
    | 'LEGACY_SYNC'
    | 'CLIENT_WORKER'
    | null;
  /** IDEXECLOG da tabela MKTPFLOWEXECLOG (Q3 - persistencia de historico). */
  idexeclog?: number | null;
  /** Modo somente-leitura ao reabrir uma execucao do historico. */
  readonly?: boolean;
}

// =====================================================================
// MKTPFLOWEXECLOG - mirror dos endpoints REST (Fase B0)
// =====================================================================

/** Payload para POST /rest/FlowExecLogServices/start. */
export interface FlowExecLogStartRequest {
  idexeclogparent?: number | null;
  codecompany: number;
  codecontract?: number | null;
  codemktp?: number | null;
  username: string;
  screenorigin: string;
  actionname: string;
  idconf?: number | null;
  strategy: string;
  label?: string | null;
  status?: string;
  rowscount?: number;
  idjob?: string | null;
  nuconf?: number | null;
  codigocab?: number | null;
  payload?: string | null;
}

export interface FlowExecLogStartResponse {
  ok: boolean;
  idexeclog?: number;
  MESSAGE?: string;
}

/** Payload para POST /rest/FlowExecLogServices/finish. */
export interface FlowExecLogFinishRequest {
  idexeclog: number;
  status: 'COMPLETED' | 'ERROR' | 'STOPPED' | 'PARTIAL';
  rowsok?: number;
  rowserr?: number;
  errormsg?: string | null;
  elapsedms?: number;
  resultsummary?: string | null;
  flowexecutionjson?: string | null;
}

/** Item retornado por /list e /get. */
export interface FlowExecLogEntry {
  idexeclog: number;
  idexeclogparent: number | null;
  codecompany: number;
  codecontract?: number | null;
  codemktp?: number | null;
  username: string;
  screenorigin: string;
  actionname: string;
  idconf: number | null;
  strategy: string;
  label: string | null;
  status: string;
  rowscount: number;
  rowsok: number;
  rowserr: number;
  errormsg: string | null;
  idjob: string | null;
  nuconf: number | null;
  codigocab: number | null;
  dtstart: string;
  dtend: string | null;
  elapsedms: number;
  /** Apenas em /get. */
  payload?: string | null;
  resultsummary?: string | null;
  flowexecutionjson?: string | null;
}

/** Filtros do GET /list. */
export interface FlowExecLogListFilter {
  codecompany: number;
  username?: string | null;
  actionname?: string | null;
  status?: string | null;
  codigocab?: number | null;
  datefrom?: string | null;
  dateto?: string | null;
  offset?: number;
  limit?: number;
}

export interface FlowExecLogListResponse {
  ok: boolean;
  total: number;
  items: FlowExecLogEntry[];
}

export interface FlowExecLogGetResponse {
  ok: boolean;
  parent: FlowExecLogEntry;
  children: FlowExecLogEntry[];
}
